import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  CartErrorEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {AbortController | null} */
  #activeCartRequest = null;
  #cartRequestSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#activeCartRequest?.abort();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#activeCartRequest?.abort();
    const abortController = new AbortController();
    this.#activeCartRequest = abortController;
    const requestSequence = ++this.#cartRequestSequence;

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const quantityInput = this.refs.quantitySelectors[line - 1]?.querySelector('input');
    const selectionStart = quantityInput?.selectionStart ?? null;
    const selectionEnd = quantityInput?.selectionEnd ?? null;
    const removalFocusKeys = config.action === 'clear' ? this.#removalFocusKeys(line) : [];

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      let response;
      try {
        response = await fetch(`${Theme.routes.cart_change_url}`, {
          ...fetchConfig('json', { body }),
          signal: abortController.signal,
        });
      } catch (_) {
        throw new Error(Theme.translations.cart_update_error);
      }
      const responseText = await response.text();
      let parsedResponseText;

      try {
        parsedResponseText = JSON.parse(responseText);
      } catch (_) {
        throw new Error(Theme.translations.cart_update_error);
      }

      if (!response.ok || !parsedResponseText || typeof parsedResponseText !== 'object') {
        throw new Error(this.#cartErrorMessage(parsedResponseText));
      }

      if (parsedResponseText.errors) {
        throw new Error(this.#cartErrorMessage(parsedResponseText));
      }

      const sectionHtml = parsedResponseText.sections?.[this.sectionId];
      if (typeof sectionHtml !== 'string' || sectionHtml.trim() === '') {
        throw new Error(Theme.translations.cart_update_error);
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const newSection = newSectionHTML.getElementById(`shopify-section-${this.sectionId}`);
      if (!newSection) throw new Error(Theme.translations.cart_update_error);

      const newCartHiddenItemCount = newSection.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      if (requestSequence !== this.#cartRequestSequence) return;
      this.#updateQuantitySelectors(parsedResponseText);
      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
      await morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
      this.#updateCartQuantitySelectorButtonStates();
      if (config.action === 'clear') this.#restoreRemovalFocus(removalFocusKeys);
    } catch (error) {
      if (error?.name === 'AbortError' || requestSequence !== this.#cartRequestSequence) return;
      const message = error instanceof Error && error.message ? error.message : Theme.translations.cart_update_error;
      this.#handleCartError(line, message, quantityInput, selectionStart, selectionEnd);
      this.dispatchEvent(new CartErrorEvent(this.sectionId, message, {}, {}));
    } finally {
      resetShimmer(this);
      if (requestSequence === this.#cartRequestSequence) {
        this.#activeCartRequest = null;
        this.#enableCartItems();
      }
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {string} message - The accessible error text.
   * @param {HTMLInputElement | null | undefined} quantityInput - The quantity input to restore.
   * @param {number | null} selectionStart - The previous selection start.
   * @param {number | null} selectionEnd - The previous selection end.
   */
  #handleCartError = (line, message, quantityInput, selectionStart, selectionEnd) => {
    if (!quantityInput) return;
    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
    quantityInput.focus();
    if (selectionStart !== null && selectionEnd !== null && typeof quantityInput.setSelectionRange === 'function') {
      quantityInput.setSelectionRange(selectionStart, selectionEnd);
    }
  };

  /** @param {any} response */
  #cartErrorMessage(response) {
    const candidate = response?.errors || response?.message || response?.description;
    return typeof candidate === 'string' && candidate.trim() ? candidate : Theme.translations.cart_update_error;
  }

  /**
   * Captures line keys in the order focus should fall back after a removal.
   * Lines after the removed row come first so bundle children that disappear
   * with a parent are skipped in favour of the next surviving garment.
   *
   * @param {number} line - The one-based cart line index.
   * @returns {string[]}
   */
  #removalFocusKeys(line) {
    const rows = this.refs.cartItemRows ?? [];
    const after = rows.slice(line).map((row) => row.dataset.key).filter(Boolean);
    const before = rows.slice(0, Math.max(0, line - 1)).reverse().map((row) => row.dataset.key).filter(Boolean);
    return [...after, ...before];
  }

  /**
   * Restores keyboard focus after authoritative cart markup has been morphed.
   *
   * @param {string[]} keys - Candidate line keys ordered by preference.
   */
  #restoreRemovalFocus(keys) {
    const components = Array.from(document.querySelectorAll('cart-items-component'));
    const component = components.find((candidate) => candidate.dataset.sectionId === this.sectionId) ?? this;
    const rows = /** @type {HTMLTableRowElement[]} */ (component.refs?.cartItemRows ?? []);
    const row = keys.map((key) => rows.find((candidate) => candidate.dataset.key === key)).find(Boolean);

    if (row) {
      const target = row.querySelector('input:not([disabled]), button:not([disabled]), a[href]') ?? row;
      if (!target.hasAttribute('tabindex') && target === row) target.setAttribute('tabindex', '-1');
      target.focus();
      return;
    }

    const drawer = component.closest('cart-drawer-component') ?? this.closest('cart-drawer-component');
    const emptyDrawerHeading = drawer?.querySelector('.cart-drawer__heading--empty');
    const drawerClose = drawer?.querySelector('.cart-drawer__close-button');
    const pageHeading = document.querySelector('.cart-title h1, #MainContent h1, main h1');
    const fallbackTarget = this.isDrawer || component.dataset.drawer !== undefined
      ? emptyDrawerHeading ?? drawerClose
      : pageHeading;

    if (fallbackTarget instanceof HTMLElement) {
      if (!fallbackTarget.hasAttribute('tabindex') && fallbackTarget === emptyDrawerHeading) {
        fallbackTarget.setAttribute('tabindex', '-1');
      }
      fallbackTarget.focus();
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    if (event instanceof DiscountUpdateEvent) {
      try {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      } catch (_) {
        this.dispatchEvent(
          new CartErrorEvent(this.sectionId, Theme.translations.cart_update_error, {}, {})
        );
      }
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      try {
        await morphSection(this.sectionId, cartItemsHtml);
        this.#updateCartQuantitySelectorButtonStates();
      } catch (_) {
        this.dispatchEvent(
          new CartErrorEvent(this.sectionId, Theme.translations.cart_update_error, {}, {})
        );
      }
    } else {
      try {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      } catch (_) {
        this.dispatchEvent(
          new CartErrorEvent(this.sectionId, Theme.translations.cart_update_error, {}, {})
        );
      }
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
